import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, Link2, Copy, Check, TrendingUp, Wallet, Loader2, ArrowUpCircle,
  MousePointerClick, UserPlus, BadgeCheck, Percent, Info, Landmark,
} from 'lucide-react';
import PaymentMethodLogo from '../../components/PaymentMethodLogo';
import client from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { buildRefLink } from '../../lib/referral';
import { accountHook } from '../../lib/domHooks';

const money = (v) => `${Number(v || 0).toFixed(2)} ₺`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-');

const Stat = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="pusu-card p-4" {...accountHook('stat')}>
    <div className="flex items-center gap-2 text-xs text-gray-400">
      <Icon size={13} className="text-[var(--pusu-gold)]" /> {label}
    </div>
    <div className={`text-2xl font-black mt-1.5 ${accent || 'gold-text'}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
  </div>
);

const TYPE_LABELS = {
  revshare: 'Gelir Payı (Yatırım − Çekim)',
  deposit: 'Yatırım Yüzdesi',
  cpa: 'Kayıt Başına (CPA)',
  manual: 'Yönetici Düzeltmesi',
};

/**
 * Ortağın kendi affiliate paneli.
 *
 * `readOnly` iş ortağı portalında (`/ortak`, pages/partner/PartnerPortal.jsx)
 * açılır: başvuru ve kazanç çekme formu çizilmez, yalnızca ortağın kendi
 * verisi (referanslar, bahisler, komisyonlar, çekim geçmişi) okunur. Veriyi
 * veren uçlar zaten yalnızca oturumdaki ortağın kaydını döndürür
 * (`/affiliate/stats`, `/affiliate/bets`); başka bir ortağın verisine giden
 * yol yoktur.
 */
const Affiliate = ({ readOnly = false }) => {
  const { setBalance } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [data, setData] = useState(null);
  const [terms, setTerms] = useState(null);
  const [copied, setCopied] = useState('');
  const [tab, setTab] = useState('referrals');
  const [withdrawConfig, setWithdrawConfig] = useState({
    min_amount: 100,
    max_amount: 100000,
  });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
  // Referansların bahis geçmişi ayrı uçtan gelir: liste uzun olabildiği için
  // yalnızca sekme açıldığında ve sayfalanarak yüklenir.
  const [bets, setBets] = useState(null);
  const [betsLoading, setBetsLoading] = useState(false);
  const [betsUser, setBetsUser] = useState('');

  // Çekim formu
  const [wAmount, setWAmount] = useState('');
  const [wMethod, setWMethod] = useState('bank');
  const [wPaymentMethodId, setWPaymentMethodId] = useState('');
  const [wIban, setWIban] = useState('');
  const [wWalletAddress, setWWalletAddress] = useState('');
  const [wBusy, setWBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: t } = await client.get('/affiliate/terms');
      setTerms(t);
    } catch { /* koşullar alınamazsa panel yine çalışır */ }
    // Salt okunur portalda çekim formu yoktur; yöntem ve limit istekleri de
    // atılmaz.
    if (!readOnly) {
      try {
        const { data: config } = await client.get('/finance/withdraw-config');
        setWithdrawConfig(config);
      } catch { /* site bakiyesine aktarım yine kullanılabilir */ }
      try {
        const { data: methodsData } = await client.get('/payments/methods', {
          params: { direction: 'withdrawal' },
          headers: { 'Cache-Control': 'no-cache' },
        });
        const availableMethods = (methodsData.items || [])
          .filter((method) => ['bank', 'crypto'].includes(method.method_type));
        setPaymentMethods(availableMethods);
        setWPaymentMethodId((current) => (
          availableMethods.some((method) => method.id === current)
            ? current
            : availableMethods[0]?.id || ''
        ));
      } catch {
        setPaymentMethods([]);
      } finally {
        setPaymentMethodsLoading(false);
      }
    }
    try {
      const { data: s } = await client.get('/affiliate/stats');
      setData(s);
    } catch (e) {
      if (e.response?.status !== 404) {
        toast({ title: 'Hata', description: e.response?.data?.detail || 'Veriler yüklenemedi' });
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [toast, readOnly]);

  useEffect(() => { load(); }, [load]);

  const loadBets = useCallback(async (filterUsername = '') => {
    setBetsLoading(true);
    try {
      const { data: result } = await client.get('/affiliate/bets', {
        params: { username: filterUsername || undefined, limit: 100 },
      });
      setBets(result);
    } catch (e) {
      toast({
        title: 'Bahis geçmişi alınamadı',
        description: e.response?.data?.detail || 'Lütfen tekrar deneyin',
      });
      setBets({ items: [], usernames: [], summary: {} });
    } finally {
      setBetsLoading(false);
    }
  }, [toast]);

  // Sekmeye ilk geçişte yüklenir; üye süzgeci değiştikçe yenilenir.
  useEffect(() => {
    if (tab === 'bets') loadBets(betsUser);
  }, [tab, betsUser, loadBets]);

  const join = async () => {
    setJoining(true);
    try {
      await client.post('/affiliate/register', {});
      toast({ title: 'Başvuru tamamlandı', description: 'Referans kodunuz oluşturuldu' });
      await load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'Başvuru başarısız' });
    } finally {
      setJoining(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: text });
    }
  };

  const submitWithdraw = async (e) => {
    e.preventDefault();
    const requestedAmount = Number(wAmount);
    const selectedMethod = paymentMethods.find(
      (method) => method.id === wPaymentMethodId,
    );
    const minimum = wMethod === 'balance'
      ? Number(data?.summary?.min_withdraw || 0)
      : Math.max(
        Number(withdrawConfig.min_amount || 0),
        Number(selectedMethod?.min_amount || 0),
      );
    const maximum = wMethod === 'balance'
      ? Number(data?.summary?.balance || 0)
      : Math.min(
        Number(withdrawConfig.max_amount || 0),
        Number(selectedMethod?.max_amount || withdrawConfig.max_amount || 0),
        Number(data?.summary?.balance || 0),
      );
    if (wMethod !== 'balance' && !selectedMethod) {
      toast({ title: 'Para çekme yöntemi seçin' });
      return;
    }
    const normalizedIban = wIban.replace(/\s/g, '').toUpperCase();
    const normalizedWallet = wWalletAddress.trim();
    if (
      wMethod !== 'balance'
      && selectedMethod?.method_type === 'bank'
      && (
        normalizedIban.length !== 26
        || !normalizedIban.startsWith('TR')
        || !/^[A-Z0-9]+$/.test(normalizedIban)
      )
    ) {
      toast({ title: 'Geçerli bir IBAN girin' });
      return;
    }
    if (
      wMethod !== 'balance'
      && selectedMethod?.method_type === 'crypto'
      && (
        normalizedWallet.length < 12
        || /\s/.test(normalizedWallet)
      )
    ) {
      toast({ title: 'Geçerli bir kripto cüzdan adresi girin' });
      return;
    }
    if (
      !Number.isFinite(requestedAmount)
      || requestedAmount < minimum
      || requestedAmount > maximum
    ) {
      toast({
        title: 'Geçersiz tutar',
        description: `${money(minimum)} ile ${money(maximum)} arasında bir tutar girin.`,
      });
      return;
    }
    setWBusy(true);
    try {
      const { data: res } = await client.post('/affiliate/withdraw', {
        amount: requestedAmount,
        method: wMethod === 'balance' ? 'balance' : selectedMethod.method_type,
        method_id: wMethod === 'balance' ? undefined : selectedMethod.id,
        iban: wMethod !== 'balance' && selectedMethod?.method_type === 'bank'
          ? normalizedIban
          : undefined,
        wallet_address: wMethod !== 'balance' && selectedMethod?.method_type === 'crypto'
          ? normalizedWallet
          : undefined,
      });
      if (res.status === 'onaylandi' && res.balance != null) setBalance(res.balance);
      toast({
        title: res.status === 'onaylandi' ? 'Aktarım tamamlandı' : 'Talep alındı',
        description: res.status === 'onaylandi'
          ? `${money(res.amount)} site bakiyenize eklendi`
          : `${money(res.amount)} ${selectedMethod.name} ile belirttiğiniz ${selectedMethod.method_type === 'crypto' ? 'cüzdan adresine' : "IBAN'a"} gönderilmek üzere incelemeye alındı`,
      });
      setWAmount('');
      if (selectedMethod?.method_type === 'bank') setWIban('');
      if (selectedMethod?.method_type === 'crypto') setWWalletAddress('');
      await load();
    } catch (e2) {
      toast({ title: 'Hata', description: e2.response?.data?.detail || 'Çekim talebi oluşturulamadı' });
    } finally {
      setWBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="pusu-panel p-10 text-center text-gray-400" {...accountHook('loading')}>
        <Loader2 className="inline animate-spin mr-2 text-[var(--pusu-gold)]" /> Yükleniyor...
      </div>
    );
  }

  // Portalda başvuru yapılmaz: ortaklık hesabını yönetici tanımlar.
  if (!data && readOnly) {
    return (
      <div className="pusu-panel p-8 text-center text-gray-400" {...accountHook('empty')}>
        <Users size={28} className="mx-auto mb-3 text-[var(--pusu-gold)]" />
        Bu hesaba tanımlı bir iş ortaklığı kaydı bulunmuyor. Yöneticinize
        başvurun.
      </div>
    );
  }

  // ---------------- Henüz affiliate değil: başvuru ekranı ----------------
  if (!data) {
    return (
      <div className="pusu-panel p-6" {...accountHook('panel')}>
        <div className="flex items-center gap-3 mb-5" {...accountHook('page-header')}>
          <Users size={20} className="text-[var(--pusu-gold)]" />
          <h2 className="text-xl font-bold gold-text" {...accountHook('title')}>Affiliate Programı</h2>
        </div>

        {terms && !terms.enabled ? (
          <div className="pusu-card p-6 text-center text-gray-400" {...accountHook('empty')}>
            Affiliate programı şu anda kapalı. Daha sonra tekrar deneyin.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" {...accountHook('summary')}>
              <div className="pusu-card p-4" {...accountHook('card')}>
                <Percent size={18} className="text-[var(--pusu-gold)] mb-2" />
                <div className="font-semibold text-sm">Yüksek Komisyon</div>
                <div className="text-xs text-gray-400 mt-1">
                  Komisyon modeliniz ve oranınız size özel tanımlanır; katıldıktan sonra bu sayfada görünür
                </div>
              </div>
              <div className="pusu-card p-4" {...accountHook('card')}>
                <Link2 size={18} className="text-[var(--pusu-gold)] mb-2" />
                <div className="font-semibold text-sm">Kişisel Davet Linki</div>
                <div className="text-xs text-gray-400 mt-1">
                  Size özel referans kodu ile {terms?.cookie_days || 30} gün boyunca takip
                </div>
              </div>
              <div className="pusu-card p-4" {...accountHook('card')}>
                <Wallet size={18} className="text-[var(--pusu-gold)] mb-2" />
                <div className="font-semibold text-sm">Kolay Ödeme</div>
                <div className="text-xs text-gray-400 mt-1">
                  Kayıtlı para çekme hesabınıza veya site bakiyenize aktarım
                </div>
              </div>
            </div>

            {terms?.terms && (
              <div className="pusu-card p-4 mb-5 flex items-start gap-3" {...accountHook('terms')}>
                <Info size={16} className="text-[var(--pusu-gold)] mt-0.5 shrink-0" />
                <div className="text-sm text-gray-400">{terms.terms}</div>
              </div>
            )}

            <button onClick={join} {...accountHook('submit')} disabled={joining} className="gold-btn px-6 py-3 rounded-xl flex items-center gap-2">
              {joining ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
              Programa Katıl ve Kod Al
            </button>
          </>
        )}
      </div>
    );
  }

  // ---------------- Affiliate paneli ----------------
  const { summary, referrals, commissions, withdraws, referral_code: code, daily } = data;
  const link = buildRefLink(code);
  // Komisyon modeli ve oranı yönetici tarafından size özel tanımlanır.
  const myCommission = (() => {
    const type = summary.commission_type || 'revshare';
    if (type === 'cpa') {
      return summary.cpa_amount
        ? `${TYPE_LABELS[type]} · ${money(summary.cpa_amount)}`
        : TYPE_LABELS[type];
    }
    const percent = type === 'deposit' ? summary.deposit_percent : summary.revshare_percent;
    return percent ? `${TYPE_LABELS[type]} · %${percent}` : TYPE_LABELS[type];
  })();
  const maxDaily = Math.max(...(daily || []).map((d) => d.amount), 1);
  const selectedPaymentMethod = paymentMethods.find(
    (method) => method.id === wPaymentMethodId,
  ) || null;
  const withdrawMinimum = wMethod === 'balance'
    ? summary.min_withdraw
    : Math.max(
      Number(withdrawConfig.min_amount || 0),
      Number(selectedPaymentMethod?.min_amount || 0),
    );
  const withdrawMaximum = wMethod === 'balance'
    ? summary.balance
    : Math.min(
      Number(withdrawConfig.max_amount || 0),
      Number(selectedPaymentMethod?.max_amount || withdrawConfig.max_amount || 0),
      Number(summary.balance || 0),
    );

  return (
    <div className="space-y-4">
      <div className="pusu-panel p-6" {...accountHook('panel')}>
        <div className="flex items-center gap-3 mb-5" {...accountHook('page-header')}>
          <Users size={20} className="text-[var(--pusu-gold)]" />
          <h2 className="text-xl font-bold gold-text" {...accountHook('title')}>Affiliate Panelim</h2>
          <span {...accountHook('badge')} className={`ml-auto badge-pill ${data.affiliate?.status === 'active' ? 'badge-green' : 'badge-gray'}`}>
            {data.affiliate?.status === 'active' ? 'Aktif' : 'Askıda'}
          </span>
        </div>

        {/* Referans kodu ve link */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5" {...accountHook('referral')}>
          <div className="pusu-card p-4" {...accountHook('card', 'code')}>
            <div className="text-xs text-gray-400 mb-2">Referans Kodunuz</div>
            <div className="flex items-center gap-2">
              <div
                className="flex-1 font-mono text-xl font-black gold-text tracking-widest"
                data-selectable="true"
              >
                {code}
              </div>
              <button onClick={() => copy(code, 'code')} className="side-icon-btn account-icon-btn inline-flex" title="Kodu kopyala">
                {copied === 'code' ? <Check size={15} className="text-green-400" /> : <Copy size={15} />}
              </button>
            </div>
          </div>
          <div className="pusu-card p-4" {...accountHook('card', 'link')}>
            <div className="text-xs text-gray-400 mb-2">Davet Linkiniz</div>
            <div className="flex items-center gap-2">
              <input readOnly value={link} className="pusu-input flex-1 text-xs" onFocus={(e) => e.target.select()} />
              <button onClick={() => copy(link, 'link')} className="side-icon-btn account-icon-btn inline-flex shrink-0" title="Linki kopyala">
                {copied === 'link' ? <Check size={15} className="text-green-400" /> : <Copy size={15} />}
              </button>
            </div>
          </div>
        </div>

        {/* Özet kartları */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" {...accountHook('summary')}>
          <Stat icon={MousePointerClick} label="Tıklama" value={summary.clicks} sub={`Dönüşüm %${summary.conversion_rate}`} accent="text-white" />
          <Stat icon={UserPlus} label="Kayıt" value={summary.signup_count} sub={`${summary.deposited_count} yatırım yaptı`} accent="text-white" />
          <Stat icon={TrendingUp} label="Toplam Kazanç" value={money(summary.total_earned)} sub={myCommission} />
          <Stat icon={Wallet} label="Çekilebilir" value={money(summary.balance)} sub={`Beklemede: ${money(summary.pending_withdraw)}`} />
        </div>
      </div>

      {/* Günlük komisyon grafiği */}
      {daily?.length > 0 && (
        <div className="pusu-panel p-6" {...accountHook('chart')}>
          <div className="text-sm font-semibold mb-4 flex items-center gap-2" {...accountHook('title')}>
            <TrendingUp size={15} className="text-[var(--pusu-gold)]" /> Son 30 Gün Komisyon
          </div>
          <div className="flex items-end gap-1 h-32">
            {daily.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative">
                {/* Yükseklik bir CSS değişkeniyle taşınır, çizim sınıftadır
                    (App.css `.account-chart-bar`); satır içi `height` ve
                    `background` panelden yazılan CSS'le ezilemiyordu. */}
                <div
                  className="account-chart-bar w-full rounded-t transition-all"
                  style={{ '--bhs-bar': `${Math.max((d.amount / maxDaily) * 100, 3)}%` }}
                  {...accountHook('chart-bar')}
                />
                <div className="account-tooltip absolute bottom-full mb-1 hidden group-hover:block whitespace-nowrap text-[10px] px-2 py-1 rounded z-10">
                  {d.date}: {money(d.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Çekim — salt okunur portalda çizilmez */}
      {!readOnly && (
      <div className="pusu-panel p-6" {...accountHook('withdraw')}>
        <div className="text-sm font-semibold mb-4 flex items-center gap-2" {...accountHook('title')}>
          <ArrowUpCircle size={15} className="text-[var(--pusu-gold)]" /> Kazanç Çekme
        </div>
        <div className="mb-4 text-xs font-bold uppercase tracking-wider text-gray-400">
          Çekim yöntemini seçin
        </div>
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" {...accountHook('methods')}>
          {paymentMethods.map((method) => {
            const selected = wMethod !== 'balance' && method.id === wPaymentMethodId;
            const methodMinimum = Math.max(
              Number(withdrawConfig.min_amount || 0),
              Number(method.min_amount || 0),
            );
            return (
              <button
                key={method.id}
                type="button"
                data-method-id={method.id}
                {...accountHook('method')}
                onClick={() => {
                  setWMethod(method.method_type);
                  setWPaymentMethodId(method.id);
                }}
                className={`flex min-h-24 items-center gap-4 rounded-xl border p-4 text-left transition ${
                  selected
                    ? 'border-[var(--pusu-gold)] bg-[var(--pusu-gold)]/[0.08] shadow-[0_0_0_1px_rgba(var(--pusu-gold-rgb),0.35)]'
                    : 'border-white/10 bg-white/[0.025] hover:border-white/20 hover:bg-white/[0.05]'
                }`}
              >
                <PaymentMethodLogo method={method} size={48} />
                <span className="min-w-0">
                  <span className="block truncate font-bold text-white">{method.name}</span>
                  <span className="mt-1 block text-xs text-gray-400">
                    {method.method_type === 'crypto' && method.network
                      ? `${method.network} · `
                      : ''}
                    Min: {money(methodMinimum)}
                  </span>
                </span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setWMethod('balance')}
            data-method-id="balance"
            {...accountHook('method')}
            className={`flex min-h-24 items-center gap-4 rounded-xl border p-4 text-left transition ${
              wMethod === 'balance'
                ? 'border-[var(--pusu-gold)] bg-[var(--pusu-gold)]/[0.08] shadow-[0_0_0_1px_rgba(var(--pusu-gold-rgb),0.35)]'
                : 'border-white/10 bg-white/[0.025] hover:border-white/20 hover:bg-white/[0.05]'
            }`}
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--pusu-gold)]/10 text-[var(--pusu-gold)]">
              <Wallet size={24} />
            </span>
            <span>
              <span className="block font-bold text-white">Site Bakiyesine Aktar</span>
              <span className="mt-1 block text-xs text-gray-400">Anında aktarım</span>
            </span>
          </button>
        </div>
        {paymentMethodsLoading && (
          <div className="mb-4 text-xs text-gray-400">Para çekme yöntemleri yükleniyor…</div>
        )}
        {!paymentMethodsLoading && paymentMethods.length === 0 && (
          <div className="mb-4 rounded-xl border border-amber-400/20 bg-amber-400/5 p-3 text-xs text-amber-200" {...accountHook('notice')}>
            Şu anda aktif para çekme yöntemi bulunmuyor. Site bakiyesine aktarımı kullanabilirsiniz.
          </div>
        )}
        {wMethod !== 'balance' && selectedPaymentMethod?.instructions && (
          <div className="mb-4 flex gap-2 rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-gray-400" {...accountHook('notice')}>
            <Info size={15} className="mt-0.5 shrink-0 text-blue-400" />
            <span>{selectedPaymentMethod.instructions}</span>
          </div>
        )}
        <form onSubmit={submitWithdraw} className="grid grid-cols-1 gap-3 md:grid-cols-3 md:items-end" {...accountHook('form')}>
          <div {...accountHook('field', 'amount')}>
            <label className="text-xs text-gray-400 block mb-1">Tutar (min {money(withdrawMinimum)})</label>
            <input type="number" step="0.01" min={withdrawMinimum} max={withdrawMaximum} value={wAmount} onChange={(e) => setWAmount(e.target.value)}
                   className="pusu-input" placeholder="0.00" required />
          </div>
          {wMethod !== 'balance' ? (
            <div {...accountHook('field', 'account')}>
              <label className="text-xs text-gray-400 block mb-1">
                {selectedPaymentMethod?.method_type === 'crypto' ? 'Cüzdan Adresi' : 'IBAN'}
              </label>
              <div className="relative">
                {selectedPaymentMethod?.method_type === 'crypto' ? (
                  <Wallet
                    size={16}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]"
                  />
                ) : (
                  <Landmark
                    size={16}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)]"
                  />
                )}
                {selectedPaymentMethod?.method_type === 'crypto' ? (
                  <input
                    type="text"
                    value={wWalletAddress}
                    onChange={(e) => setWWalletAddress(e.target.value)}
                    maxLength={255}
                    autoComplete="off"
                    placeholder={`${selectedPaymentMethod.network || 'Kripto'} cüzdan adresinizi girin...`}
                    className="pusu-input pl-10"
                    required
                  />
                ) : (
                  <input
                    type="text"
                    value={wIban}
                    onChange={(e) => setWIban(e.target.value.toUpperCase())}
                    maxLength={32}
                    autoComplete="off"
                    placeholder="IBAN girin..."
                    className="pusu-input pl-10 uppercase"
                    required
                  />
                )}
              </div>
            </div>
          ) : (
            <div {...accountHook('field', 'transfer')}>
              <label className="text-xs text-gray-400 block mb-1">Aktarım</label>
              <div className="pusu-input flex items-center text-xs text-gray-300">
                Komisyon bakiyeniz site bakiyesine eklenir
              </div>
            </div>
          )}
          <button
            type="submit"
            disabled={
              wBusy
              || Number(withdrawMaximum) < Number(withdrawMinimum)
              || (wMethod !== 'balance' && !selectedPaymentMethod)
            }
            className="gold-btn py-3 rounded-xl flex items-center justify-center gap-2"
            {...accountHook('submit')}
          >
            {wBusy ? <Loader2 size={15} className="animate-spin" /> : <ArrowUpCircle size={15} />} Talep Oluştur
          </button>
        </form>
        <div className="text-xs text-gray-500 mt-3">
          Çekilebilir bakiye: <span className="gold-text font-bold">{money(summary.balance)}</span>
          {' · '}Maksimum: {money(withdrawMaximum)}
          {' · '}Toplam çekilen: {money(summary.total_withdrawn)}
        </div>
      </div>
      )}

      {/* Sekmeler */}
      <div className="pusu-panel p-6" {...accountHook('list')}>
        <div className="flex gap-2 mb-4 flex-wrap" {...accountHook('tabs')}>
          {[
            { id: 'referrals', label: `Referanslarım (${referrals.length})` },
            { id: 'bets', label: 'Bahis Geçmişi' },
            { id: 'commissions', label: `Komisyonlar (${commissions.length})` },
            { id: 'withdraws', label: `Çekimler (${withdraws.length})` },
          ].map((t) => (
            <button key={t.id} data-bhs-tab={t.id} {...accountHook('tab')} onClick={() => setTab(t.id)} className={`provider-chip ${tab === t.id ? 'active' : ''}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          {tab === 'referrals' && (
            <table className="w-full text-sm min-w-[760px]" {...accountHook('table')}>
              <thead>
                <tr className="text-xs text-gray-400 border-b border-white/5">
                  <th className="text-left py-2">ÜYE</th><th className="text-left py-2">DURUM</th>
                  <th className="text-right py-2">YATIRIM</th><th className="text-right py-2">ÇEKİM</th>
                  <th className="text-right py-2">NET FARK</th><th className="text-right py-2">KOMİSYON</th>
                  <th className="text-right py-2">KAYIT</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r) => (
                  <tr key={r.id} className="border-b border-white/5" {...accountHook('row')}>
                    <td className="py-2.5 font-semibold">{r.username}</td>
                    <td className="py-2.5">
                      <span className={`badge-pill ${r.status === 'qualified' ? 'badge-green' : 'badge-gray'}`}>
                        {r.status === 'qualified' ? <><BadgeCheck size={10} /> Yatırım Yaptı</> : 'Kayıtlı'}
                      </span>
                    </td>
                    <td className="py-2.5 text-right text-gray-300">{money(r.total_deposit)}</td>
                    <td className="py-2.5 text-right text-red-300">{money(r.total_withdraw)}</td>
                    <td className={`py-2.5 text-right ${Number(r.commissioned_base || 0) >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>{money(r.commissioned_base)}</td>
                    <td className={`py-2.5 text-right font-bold ${Number(r.commission_earned || 0) >= 0 ? 'gold-text' : 'text-red-400'}`}>{money(r.commission_earned)}</td>
                    <td className="py-2.5 text-right text-gray-500 text-xs">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
                {referrals.length === 0 && (
                  <tr {...accountHook('empty')}><td colSpan={7} className="text-center py-8 text-gray-500">
                    Henüz referansınız yok. Davet linkinizi paylaşmaya başlayın.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === 'bets' && (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4" {...accountHook('filters')}>
                <select
                  value={betsUser}
                  onChange={(e) => setBetsUser(e.target.value)}
                  className="pusu-input max-w-[240px]"
                  aria-label="Üyeye göre süz"
                >
                  <option value="">Tüm referanslarım</option>
                  {(bets?.usernames || []).map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                {bets?.summary && (
                  <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                    <span>Toplam bahis: <b className="text-gray-200">{money(bets.summary.total_bet)}</b></span>
                    <span>Toplam kazanç: <b className="text-gray-200">{money(bets.summary.total_win)}</b></span>
                    <span>Net kayıp: <b className="gold-text">{money(bets.summary.net_loss)}</b></span>
                  </div>
                )}
              </div>
              {betsLoading ? (
                <div className="py-10 text-center text-gray-500" {...accountHook('loading')}>Yükleniyor...</div>
              ) : (
                <table className="w-full text-sm min-w-[680px]" {...accountHook('table')}>
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-white/5">
                      <th className="text-left py-2">TARİH</th><th className="text-left py-2">ÜYE</th>
                      <th className="text-left py-2">OYUN</th><th className="text-right py-2">BAHİS</th>
                      <th className="text-right py-2">KAZANÇ</th><th className="text-right py-2">NET</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bets?.items || []).map((row) => (
                      <tr key={row.id} className="border-b border-white/5" {...accountHook('row')}>
                        <td className="py-2.5 text-gray-500 text-xs">{fmtDate(row.created_at)}</td>
                        <td className="py-2.5 font-semibold">{row.username}</td>
                        <td className="py-2.5 text-gray-300">
                          {row.game_name || '-'}
                          {row.provider_name && (
                            <span className="block text-xs text-gray-500">{row.provider_name}</span>
                          )}
                        </td>
                        <td className="py-2.5 text-right text-gray-300">{money(row.bet)}</td>
                        <td className="py-2.5 text-right text-green-400">{money(row.win)}</td>
                        <td className={`py-2.5 text-right font-bold ${row.net >= 0 ? 'gold-text' : 'text-red-400'}`}>
                          {money(row.net)}
                        </td>
                      </tr>
                    ))}
                    {(bets?.items || []).length === 0 && (
                      <tr {...accountHook('empty')}><td colSpan={6} className="text-center py-8 text-gray-500">
                        Referanslarınızın henüz bahis kaydı yok.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              )}
              {bets?.total > (bets?.items || []).length && (
                <div className="mt-3 text-center text-xs text-gray-500">
                  Son {bets.items.length} tur gösteriliyor · toplam {bets.total}
                </div>
              )}
            </div>
          )}

          {tab === 'commissions' && (
            <table className="w-full text-sm min-w-[560px]" {...accountHook('table')}>
              <thead>
                <tr className="text-xs text-gray-400 border-b border-white/5">
                  <th className="text-left py-2">TARİH</th><th className="text-left py-2">TÜR</th>
                  <th className="text-left py-2">ÜYE</th><th className="text-right py-2">TABAN</th>
                  <th className="text-right py-2">TUTAR</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.id} className="border-b border-white/5" {...accountHook('row')}>
                    <td className="py-2.5 text-gray-400 text-xs">{fmtDate(c.created_at)}</td>
                    <td className="py-2.5">{TYPE_LABELS[c.type] || c.type}{c.percent ? ` (%${c.percent})` : ''}</td>
                    <td className="py-2.5 text-gray-300">{c.referred_username || '-'}</td>
                    <td className="py-2.5 text-right text-gray-400">{money(c.base_amount)}</td>
                    <td className={`py-2.5 text-right font-bold ${Number(c.amount || 0) >= 0 ? 'gold-text' : 'text-red-400'}`}>{money(c.amount)}</td>
                  </tr>
                ))}
                {commissions.length === 0 && (
                  <tr {...accountHook('empty')}><td colSpan={5} className="text-center py-8 text-gray-500">Henüz komisyon kaydı yok</td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === 'withdraws' && (
            <table className="w-full text-sm min-w-[560px]" {...accountHook('table')}>
              <thead>
                <tr className="text-xs text-gray-400 border-b border-white/5">
                  <th className="text-left py-2">TARİH</th><th className="text-left py-2">YÖNTEM</th>
                  <th className="text-left py-2">DURUM</th><th className="text-right py-2">TUTAR</th>
                  <th className="text-left py-2">NOT</th>
                </tr>
              </thead>
              <tbody>
                {withdraws.map((w) => (
                  <tr key={w.id} className="border-b border-white/5" data-bhs-status={w.status} {...accountHook('row')}>
                    <td className="py-2.5 text-gray-400 text-xs">{fmtDate(w.created_at)}</td>
                    <td className="py-2.5">
                      {w.method === 'balance' ? 'Site Bakiyesi' : w.method === 'crypto' ? 'Kripto' : 'Banka'}
                    </td>
                    <td className="py-2.5">
                      <span className={`badge-pill ${w.status === 'onaylandi' ? 'badge-green' : w.status === 'reddedildi' ? 'badge-red' : 'badge-yellow'}`}>
                        {w.status}
                      </span>
                    </td>
                    <td className="py-2.5 text-right font-bold">{money(w.amount)}</td>
                    <td className="py-2.5 text-gray-500 text-xs">{w.admin_note || '-'}</td>
                  </tr>
                ))}
                {withdraws.length === 0 && (
                  <tr {...accountHook('empty')}><td colSpan={5} className="text-center py-8 text-gray-500">Henüz çekim talebi yok</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Affiliate;
